import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TeamBadge from "./TeamBadge";
import { getAvatarColor } from "../utils/common";
import { dashboardActiveTasksApis } from "../redux/dashboard/dashboardActions";
import {
  selectActiveTasks,
  selectActiveTasksStatus,
} from "../redux/dashboard/dashboardSelectors";
import "../assets/css/activeTasks.css";

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/* Minutes as people say them. "142m" is a number you have to convert before
   it means anything; "2h 22m" is already the thought. */
const dur = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (m < 1) return "just started";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return r ? `${h}h ${r}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

/**
 * Who is working on what, right now.
 *
 * One row per In Progress task: the person, the task, and how long it has
 * been running against what it was estimated at. The bar is the point — a
 * list of elapsed times tells you nothing on its own, because two hours is
 * fine on a two-hour task and alarming on a twenty-minute one.
 *
 * Sorted longest-running first. The reason to open this panel is to find the
 * task nobody has touched since Tuesday, and that one must not be below the
 * fold.
 */
const ActiveTasksPanel = ({ pollMs = 60000 }) => {
  const dispatch = useDispatch();
  const rows = useSelector(selectActiveTasks);
  const status = useSelector(selectActiveTasksStatus);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    dispatch(dashboardActiveTasksApis());
    if (!pollMs) return undefined;
    const id = setInterval(() => dispatch(dashboardActiveTasksApis()), pollMs);
    return () => clearInterval(id);
  }, [dispatch, pollMs]);

  /* The elapsed figures come from the server, but a panel left open would sit
     there showing "1h 3m" all afternoon. Ticking a local clock every 30s and
     adding the drift keeps the numbers honest between polls without asking
     the server more often. */
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const tasks = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list.map((r) => {
      const started = r.StartedAt ? new Date(r.StartedAt).getTime() : null;
      const elapsed = started
        ? Math.max(0, Math.round((now - started) / 60000))
        : Number(r.ElapsedMinutes) || 0;
      const est = Number(r.EstimateMinutes) || 0;
      return {
        ...r,
        elapsed,
        est,
        /* No estimate means no judgement to make — the bar stays neutral
           rather than implying an overrun against a target nobody set. */
        pct: est > 0 ? Math.min(100, Math.round((elapsed / est) * 100)) : null,
        over: est > 0 && elapsed > est,
      };
    });
  }, [rows, now]);

  const overrunning = tasks.filter((t) => t.over).length;

  return (
    <div className="content-card atp">
      <div className="atp__head">
        <h5 className="atp__heading">
          <i className="bi bi-broadcast" />
          Working On Now
        </h5>
        <div className="atp__headmeta">
          {overrunning > 0 && (
            <span className="atp__overpill">
              <i className="bi bi-exclamation-triangle-fill" />
              {overrunning} over estimate
            </span>
          )}
          <span className="atp__count">
            {status === "loading" && !tasks.length
              ? "Loading…"
              : `${tasks.length} running`}
          </span>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="atp__empty">
          <i className="bi bi-cup-hot" />
          <p>Nobody has a task running right now.</p>
          <span>
            A task appears here the moment somebody sets it to In Progress.
          </span>
        </div>
      ) : (
        <div className="atp__list">
          {tasks.map((t) => (
            <article
              key={t.QueryCode}
              className={`atp__row${t.over ? " is-over" : ""}`}
            >
              <span
                className="atp__avatar"
                style={{ background: t.UserColor || getAvatarColor(t.UserName) }}
                title={t.UserName}
              >
                {initials(t.UserName)}
              </span>

              <div className="atp__body">
                <div className="atp__line1">
                  <span className="atp__who">{t.UserName}</span>
                  {t.TeamName && (
                    <TeamBadge name={t.TeamName} short={t.TeamShortName} />
                  )}
                  {t.QueryNo && (
                    <span className="atp__no">#{t.QueryNo}</span>
                  )}
                </div>

                <div className="atp__task" title={t.NatureofQuery || t.TaskName}>
                  {t.TaskName || t.NatureofQuery || "Untitled task"}
                </div>

                <div className="atp__meta">
                  {t.ClientName && (
                    <span>
                      <i className="bi bi-building" />
                      {t.ClientName}
                    </span>
                  )}
                  {t.FormName && (
                    <span>
                      <i className="bi bi-list-nested" />
                      {t.FormName}
                    </span>
                  )}
                </div>

                {/* Elapsed against estimate. The bar carries the comparison so
                    the two numbers underneath do not have to be done in your
                    head. */}
                <div className="atp__track" aria-hidden="true">
                  <span
                    className="atp__fill"
                    style={{ width: `${t.pct ?? 6}%` }}
                  />
                </div>
                <div className="atp__times">
                  <span className="atp__elapsed">{dur(t.elapsed)}</span>
                  <span className="atp__of">
                    {t.est > 0 ? `of ${dur(t.est)} estimated` : "no estimate set"}
                  </span>
                  {t.over && (
                    <span className="atp__overby">
                      +{dur(t.elapsed - t.est)} over
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveTasksPanel;
